/**
 * Award-data SSOT — Aged Care Award MA000018
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ISOLATION GUARANTEE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * This file encodes **MA000018-specific constants ONLY**. DO NOT add fields
 * from other awards' provisions even if they share field names. Cross-award
 * shape-porting risk per `project_cross_award_shape_porting_risk.md` is
 * mitigated by this isolation. See runbook §7.
 *
 * Aged Care recall is UNIFIED at cl 25.1(e) (4h flat, no in-person/electronic
 * split, no travel-counts, no within-3h carveout). Mirrors HPSS cl 25.6
 * and SCHADS cl 28.4. Does NOT mirror Nurses MA000034 cl 19.6/19.7 split
 * shape — that pattern was the 3rd confirmed cross-award shape-porting
 * defect (errata #16; Phase 1K rewrite).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Primary sources for every value below:
 *   - FWC consolidated MA000018 (accessed via ANMF AM2020/99 submission
 *     corpus when fwc.gov.au PDF viewer truncates cl 22+)
 *   - FWO Pay Guide G01002985 (republished 15 Jan 2026)
 *   - FWC determination PR791119 (Aged Care direct-care Stage 3, ppc 1 Oct 2025)
 *   - FWC determinations PR786744 + PR786745 (wage-related allowances ppc 1 Jul 2025)
 *
 * Aged Care rate data lives in DB (`award_rate_lines`) and is consumed by
 * the engine via `RateContext`. This file carries the engine's structural
 * RULES (recall, shift classification, classification streams, disturbance)
 * that are NOT in the DB rate-lines table.
 *
 * AWR 2026 hard deadline for reseed: 22 Jun 2026.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * KNOWN GAPS (Madea-blocking — see CLAUDE.md "Known platform gaps")
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   - cl 22.9(j) "8 consecutive hours off → double rate" failsafe (W5).
 *   - cl 25.1(d) "10-hour rest break OT failsafe" (W11; analogue to cl 22.9(j),
 *     casuals excluded).
 *   - UNIFORM weekly cap $6.24/wk + LAUNDRY weekly cap $1.49/wk aggregation
 *     (W4.1) — same pay-run-level pattern as NAUSEOUS_WORK_MIN.
 *
 * W4 (2026-05-17) ADDED: NAUSEOUS_WORK + MEAL + MEAL_OT_ADDITIONAL emission
 * paths (cl 15.5(a) + cl 15.4(a)(i) + cl 15.4(a)(ii)). See
 * `AGED_CARE_NAUSEOUS_WORK_EMISSION` + `AGED_CARE_MEAL_OT_EMISSION` below.
 * Engine 5.5.6 → 5.5.7.
 *
 * W4.0.5 (2026-05-17) ADDED: cl 15.3 LEADING_HAND all-purpose base-rate lift
 * (4 tiers). See `AGED_CARE_LEADING_HAND_EMISSION` + `resolveLeadingHandTier`
 * below. Per cl 15.3(c) verbatim "part of salary for all purposes": the
 * per-hour amount is ADDED to `baseHourlyRate` BEFORE every penalty/OT/shift-
 * loading multiplier — STRUCTURAL change to engine `calculate()` flow, not
 * a flat allowance line. Engine 5.5.7 → 5.5.8.
 */

import type { OtRateBands, RecallRules, ShiftCharacterRules, DisturbanceRules } from './_shared';

// ═══════════════════════════════════════════════════════════════════════
// Shift classification rules (cl 26.1) — Phase Codex Pass A 2026-05-19
// ═══════════════════════════════════════════════════════════════════════

/**
 * Aged Care MA000018 cl 26.1 commencement-band classification rules.
 *
 * Phase 1A.4 (2026-05-16) — first cut, finish-only Nurses-shape port
 *   `{ afternoon: { finishAfterHour: 18, finishAtOrBeforeHour: 24 }, ... }`
 *   was structurally wrong for MA000018. cl 26.1 is COMMENCEMENT-based
 *   with FOUR bands (10% / 12.5% / 15% / 10%), not finish-based with
 *   two bands.
 *
 * Phase Codex Pass A (2026-05-19) — REBUILT per cl 26.1 primary-source
 *   verification. The AC engine now calls `classifyAgedCareCommencementBand`
 *   directly with `startTime` and maps to one of 4 bands. The generic
 *   `ShiftCharacterRules` shape used by the shared `classifyShift` is
 *   intentionally EMPTY (no afternoon/night/orTrigger) — `classifyShift`
 *   returns `shiftCharacter: 'DAY'` for AC, and the engine derives the
 *   commencement band itself.
 *
 * Verbatim cl 26.1 (FWC consolidated MA000018 + FWO PayCat summary +
 * Tanda MA000018 managed template confirm verbatim):
 *
 *   "Afternoon shift commencing at 10.00 am and before 1.00 pm: 10%
 *    Afternoon shift commencing at 1.00 pm and before 4.00 pm: 12.5%
 *    Night shift commencing at 4.00 pm and before 4.00 am: 15%
 *    Night shift commencing at 4.00 am and before 6.00 am: 10%"
 *
 * Pre-Phase-Codex-A wage-exposure direction (P0 under-pay):
 *   - 1pm-9:30pm shift: pre-Phase classified as 10% afternoon (finish
 *     after 18:00); correct is 12.5% afternoon (commences 1pm-4pm) →
 *     UNDER-pay 2.5% × ordinary × shift-hours.
 *   - 4pm-midnight shift: pre-Phase classified as 10% afternoon (finish
 *     in 18-24 band); correct is 15% night (commences 4pm-4am) →
 *     UNDER-pay 5% × ordinary × shift-hours.
 *   - 4am-6am commence (e.g. 04:30-12:30 early morning): pre-Phase
 *     classified as DAY (no finish in night band); correct is 10%
 *     early-morning band (commences 4am-6am) → UNDER-pay 10% × ordinary.
 *
 * FW Act s.327A wage-theft criminal regime exposure (max 10y prison /
 * AU$8.25M corp fines).
 *
 * Source: FWC consolidated MA000018 cl 26.1.
 * Verified: Phase Codex Pass A (2026-05-19) statura-regulatory-source-agent.
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
 *
 * The 4-band loading values + classifier helper are exported from
 * `award-engines/shift-classifier.ts` (`AGED_CARE_COMMENCEMENT_BAND_LOADINGS`
 * + `classifyAgedCareCommencementBand`). DB seed
 * `supabase/migrations/20260608100000_seed_aged_care_v1.sql:172-184`
 * carries 2-band `WEEKDAY_EVENING` / `WEEKDAY_NIGHT` rows that match
 * the 12.5% / 15% values. The 10% bands are not separately seeded;
 * the engine reads loading percentages directly from
 * `AGED_CARE_COMMENCEMENT_BAND_LOADINGS` (primary-source verified).
 * AWR 2026 reseed window (22 Jun 2026) MUST re-verify these loadings
 * against the post-AWR-2026 pay guide.
 */
export const AGED_CARE_SHIFT_RULES: ShiftCharacterRules = {
  // Intentionally empty — AC commencement-band classification is performed
  // in the engine via `classifyAgedCareCommencementBand` (cl 26.1, 4 bands).
  // The generic `classifyShift` returns `shiftCharacter: 'DAY'` for AC.
};

// ═══════════════════════════════════════════════════════════════════════
// Recall-to-work rules (cl 25.1(e)) — Phase 1K primary-source rewrite
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1K (Award Engine Integrity 2026-05-16) — Aged Care recall-to-work
 * rules per MA000018 cl 25.1(e).
 *
 * Corrects Phase 1C commit which shipped `AGED_CARE_RECALL_RULES` as a
 * Nurses MA000034 cl 19.6/19.7 shape copy (3h in-person + 1h electronic
 * + travel-counts + within-3h carveout) with the clauseRef `cl 22`
 * (wrong — cl 22 is "Ordinary hours of work and rostering"; recall is
 * at cl 25.1(e)). Phase 1.4 Cluster B sweep flagged the 4h minimum via
 * secondary-source calculators (Tanda + Paycat); Phase 1K primary-source
 * verification confirmed and additionally established that MA000018 has
 * **no electronic-recall, no travel-counts, no within-3h carveout** —
 * these were all fabricated Nurses-shape ports (3rd confirmed cross-award
 * shape-porting instance).
 *
 * Verbatim cl 25.1(e) (FWC consolidated MA000018, ANMF AM2020/99 corpus):
 *
 *     "An employee recalled to work overtime after leaving the employer's
 *      premises will be paid for a minimum of four hours' work at the
 *      appropriate rate for each time so recalled. If the work required
 *      is completed in less than four hours, the employee will be
 *      released from duty."
 *
 *   - **4h flat minimum** at OT rate.
 *   - **No electronic-recall provision** — electronic input pays the 4h floor.
 *   - **No travel-counts-as-time-worked** — MA000018 has no equivalent.
 *   - **No within-rostered-start carveout** — MA000018 has no equivalent.
 *   - Casual loading is INCLUSIVE per cl 10.4(b).
 *
 * Wage-theft exposure under the pre-fix Phase 1C rules:
 *   - In-person recall: 3h paid, should be 4h → **under-payment of 1h
 *     at OT rate per recall event** (P0 FW Act s.327A exposure).
 *
 * Source: FWC consolidated MA000018 cl 25.1(e) + Phase 1K primary-source verification.
 * Verified: 2026-05-16 (Phase 1K).
 * Artifact: docs/operations/clause-verifications/2026-05-16-aged-care-legislation-aged-care-recall-primary-source-verification.md
 * Errata: #16 (cross-award shape-porting + 3 fabricated provisions).
 */
/**
 * Phase Codex Pass A PR #B (2026-05-19) — Aged Care MA000018 OT rate
 * bands for banded recall pay per cl 25.1(a) read with cl 25.1(e).
 *
 * Verbatim cl 25.1(a) (FWC consolidated MA000018, specialist
 * primary-source verification 2026-05-19):
 *
 *   "(a) Each hour of overtime worked must be paid at the following
 *        rates of the minimum hourly rate:
 *        (i)  Monday to Friday—
 *             (A) first 2 hours—150%; and
 *                 after 2 hours—200%; and
 *             (B) Saturday or Sunday—200%; and
 *        (ii) on a public holiday—250%."
 *
 *   "NOTE: For a casual employee, the rates in this clause are
 *    calculated by adding the casual loading prescribed in clause 11
 *    to the percentages in (a) before applying the overtime rates."
 *
 * Casual loading is MULTIPLICATIVE per the explicit NOTE — casual
 * tier-1 = 150% × 1.25 = 187.5%; casual tier-2 = 200% × 1.25 = 250%;
 * casual Saturday/Sunday = 200% × 1.25 = 250%; casual PH = 250% ×
 * 1.25 = 312.5%.
 *
 * **CRITICAL FINDING (specialist §1 + §3, 2026-05-19):** Saturday is
 * FLAT 200% (cl 25.1(a)(i)(B)), NOT banded. Saturday is grouped with
 * Sunday in the same sub-clause. This differs from the Nurses /
 * HPSS / SCHADS pattern where Saturday joins the Mon-Sat tier curve.
 * Cross-award shape-porting risk: this is the 9th confirmed instance
 * (see project_cross_award_shape_porting_risk.md). DO NOT port the
 * Nurses/HPSS/SCHADS shape onto AC — Saturday recall under the
 * tiered shape would UNDER-pay first-2h (1.5x → should be flat 2.0x).
 *
 * Source: FWC consolidated MA000018 cl 25.1 (PDF text extraction
 *         via pdftotext) + Pay Guide G01002982 OT section.
 * Verified: 2026-05-19 (Phase Codex Pass A PR #B specialist invocation;
 *           statura-regulatory-source-agent autonomous mode).
 * Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
 * Errata: #34 (Codex P0-1/P0-2 amplification — pre-fix collapsed banded
 *         math to flat 1.5x).
 */
export const AGED_CARE_OT_RATE_BANDS: OtRateBands = {
  weekdayTier1Hours: 2, // cl 25.1(a)(i)(A) "first 2 hours"
  weekdayTier1: {
    permanent: 1.5, // cl 25.1(a)(i)(A) Mon-Fri first 2h — 150%
    casual: 1.875, // cl 25.1 NOTE: 1.5 × 1.25 (casual loading per cl 11)
  },
  weekdayTier2: {
    permanent: 2.0, // cl 25.1(a)(i)(A) Mon-Fri after 2h — 200%
    casual: 2.5, // cl 25.1 NOTE: 2.0 × 1.25
  },
  // Saturday FLAT 200% per cl 25.1(a)(i)(B) — NOT banded with weekday.
  saturdayFlat: {
    permanent: 2.0, // cl 25.1(a)(i)(B) Saturday flat — 200%
    casual: 2.5, // cl 25.1 NOTE: 2.0 × 1.25
  },
  sunday: {
    permanent: 2.0, // cl 25.1(a)(i)(B) Sunday flat — 200%
    casual: 2.5, // cl 25.1 NOTE: 2.0 × 1.25
  },
  publicHoliday: {
    permanent: 2.5, // cl 25.1(a)(ii) PH — 250%
    casual: 3.125, // cl 25.1 NOTE: 2.5 × 1.25
  },
  clauseRefs: {
    weekdayTier1:
      'Aged Care Award MA000018 cl 25.1(e) recall trigger + cl 25.1(a)(i)(A) (Mon-Fri OT first 2 hours — 150%)',
    weekdayTier2:
      'Aged Care Award MA000018 cl 25.1(e) recall trigger + cl 25.1(a)(i)(A) (Mon-Fri OT after 2 hours — 200%)',
    saturdayFlat:
      'Aged Care Award MA000018 cl 25.1(e) recall trigger + cl 25.1(a)(i)(B) (Saturday OT flat — 200%, grouped with Sunday)',
    sunday:
      'Aged Care Award MA000018 cl 25.1(e) recall trigger + cl 25.1(a)(i)(B) (Sunday OT flat — 200%)',
    publicHoliday:
      'Aged Care Award MA000018 cl 25.1(e) recall trigger + cl 25.1(a)(ii) (PH OT flat — 250%)',
  },
};

export const AGED_CARE_RECALL_RULES: RecallRules = {
  awardCode: 'MA000018',
  inPersonMinimumHours: 4,
  electronicMinimumHours: 4,
  travelCountsAsTimeWorked: false,
  electronicRoundingMinutes: 15,
  withinRosteredStartCarveoutHours: 0,
  // Phase Codex Pass A PR #B (2026-05-19) — corrected from 'INCLUSIVE' to
  // 'MULTIPLICATIVE' per AC cl 25.1 NOTE primary-source verification
  // (specialist artifact §1 + §5). The 'INCLUSIVE' setting pre-fix
  // silently underpaid casuals by 25% on the OT multiplier because the
  // ctx penalty band's CASUAL row carries the ORDINARY-hour casual rate
  // (e.g. cl 26.4 Sunday ordinary 1.75x for casual) NOT the OT casual
  // rate (cl 25.1(e) read with cl 25.1(a) tier-2 = 2.5x for casual).
  // The new `otBands` table below carries the FINAL casual rates
  // directly so this field is now informational. Retained for any
  // legacy caller that hasn't migrated.
  casualLoadingTreatment: 'MULTIPLICATIVE',
  otBands: AGED_CARE_OT_RATE_BANDS,
  clauseRef:
    'Aged Care Award MA000018 cl 25.1(e) (recall to work overtime — unified provision; no in-person/electronic split, no travel-counts, no within-3h carveout) read with cl 25.1(a) banded OT rates',
};

// ═══════════════════════════════════════════════════════════════════════
// Classification streams
// ═══════════════════════════════════════════════════════════════════════

/** Classification streams used by the Aged Care engine. */
export const AGED_CARE_STREAMS: ReadonlySet<string> = new Set([
  'aged_care_employee',
  'direct_care',
  'support_services',
  // Tenant EA overlays (e.g. Medea Park MA000018 General Staff EA) store
  // rate lines under a dedicated stream key while still using AgedCareEngine.
  'GENERAL_STAFF_EA',
]);

// ═══════════════════════════════════════════════════════════════════════
// Disturbance-during-sleepover rules (cl 22.9(f) read with cl 22.9(g))
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase 1I.1b (Award Engine Integrity 2026-05-16) — Aged Care disturbance
 * during sleepover per MA000018 cl 22.9(f) read with cl 22.9(g).
 *
 * Corrects Phase 1E commit which shipped `AGED_CARE_DISTURBANCE_RULES` as
 * a copy of SCHADS PER_EVENT 1h-minimum-at-OT shape with the qualifier
 * `'Aged Care Award MA000018 cl 22 (subclause to verify pre-prod)'`. The
 * Phase 1I.1b primary-source verification established the entire model
 * is structurally wrong for MA000018:
 *
 *   - cl 22.9(f) uses a LESSER_OF_WINDOW model: paid time =
 *     min(sleepoverStart → end-of-last-work, start-of-first-work → sleepoverEnd).
 *     A 15-min disturbance at 02:00 in an 8h sleepover pays 4 hours under
 *     the Award, not 1h.
 *   - OT is only mandatory for full-time workers (cl 22.9(g)(i)).
 *     PPT and casual get ordinary pay plus shift/weekend penalties, with
 *     OT only above threshold conditions.
 *   - No-collapse is structurally incompatible: cl 22.9(f) collapses
 *     multiple disturbances into one window by construction.
 *   - 15-min round-up has no Award authority (cosmetic worker-favouring).
 *
 * Aged Care has NO Award-text minimum and NO Award-text rounding — these
 * fields are present for interface symmetry with the SCHADS PER_EVENT
 * model. Engine implementation routes through `DisturbanceModel:
 * 'LESSER_OF_WINDOW'` discriminator.
 *
 * cl 22.9(j) 8h-off failsafe is NOT implemented — tracked as W5 known-gap
 * (CLAUDE.md "Known platform gaps").
 *
 * Source: FWC consolidated MA000018 cl 22.9(f) + cl 22.9(g) + cl 22.9(j) deferred.
 * Verified: 2026-05-16 (Phase 1I.1b).
 * Artifact: docs/operations/clause-verifications/2026-05-16-aged-care-legislation-aged-care-disturbance-subclause.md
 */
export const AGED_CARE_DISTURBANCE_RULES: DisturbanceRules = {
  awardCode: 'MA000018',
  model: 'LESSER_OF_WINDOW',
  minimumHours: 0, // No Award-text minimum; field present for interface symmetry
  roundingMinutes: 0, // No Award-text rounding; field present for interface symmetry
  casualLoadingTreatment: 'INCLUSIVE',
  clauseRef: 'Aged Care Award MA000018 cl 22.9(f) read with cl 22.9(g)',
};

// ═══════════════════════════════════════════════════════════════════════
// Nauseous work emission semantics (cl 15.5(a)) — Phase W4 (2026-05-17)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase W4 (Award Engine Integrity 2026-05-17) — Aged Care nauseous-work
 * allowance emission semantics per MA000018 cl 15.5(a).
 *
 * Verbatim cl 15.5(a) (FWC consolidated MA000018, primary-source verified
 * Phase 1I.5 + W4 specialist invocations):
 *
 *     "An employee performing the following work shall be paid a further
 *      $0.60 per hour or part thereof with a minimum of $3.24 per week:
 *        (i)  handling linen of a nauseous nature; or
 *        (ii) work which is of an unusually dirty or offensive nature."
 *
 * Engine emission rule (W4 scope):
 *   - Per-shift emission only. If `input.nauseousWorkHours > 0`, emit a
 *     `NAUSEOUS_WORK` allowance line at `$0.60 × Math.ceil(nauseousWorkHours)`.
 *   - "or part thereof" semantically rounds partial hours UP to a full hour.
 *   - NOT all-purpose — Pay Guide page 24 row carries NO asterisk. The
 *     amount is paid FLAT regardless of penalty band / OT / casual loading.
 *     The casual loading does NOT compound on this allowance.
 *
 * Weekly minimum (resolved from the effective catalogue) is a CROSS-SHIFT aggregator —
 * pay-run-level logic, not a per-shift top-up. The engine carries the resolved minimum.
 *
 * **Cross-award native symmetry (binding per runbook §7 per-award isolation)**:
 * HPSS Award MA000027 cl 18.8 has STRUCTURALLY IDENTICAL nauseous-work
 * shape (same 0.05%/hr + 0.27%/wk min model). Different standard-rate
 * anchor ($1,199.00 vs $939.80) produces different absolute amounts. Per
 * `project_cross_award_shape_porting_risk.md`, the symmetry is genuine but
 * the constants live INDEPENDENTLY in each award's SSOT file — DO NOT
 * factor `NauseousRules` into `_shared.ts`. Future FWC variations may
 * diverge; the per-award isolation prevents maintenance drift.
 * Nurses MA000034 and SCHADS MA000100 have NO equivalent allowance.
 *
 * Source: FWC consolidated MA000018 cl 15.5(a) + FWO Pay Guide G01002985
 * (republished 15 Jan 2026) page 24.
 * Verified: 2026-05-17 (W4 aged-care-legislation specialist).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-aged-care-production-readiness-emission-paths.md
 */
export const AGED_CARE_NAUSEOUS_WORK_EMISSION = {
  awardCode: 'MA000018' as const,
  /** DB lookup key for the per-hour rate (PER_HOUR basis, $0.60 verified Phase 1I.5). */
  hourlyAllowanceCode: 'NAUSEOUS_WORK' as const,
  /** DB lookup key for the weekly minimum (W4.1 aggregator; engine ignores in W4). */
  weeklyMinAllowanceCode: 'NAUSEOUS_WORK_MIN' as const,
  /** "per hour or part thereof" — Math.ceil(hours) for the per-shift amount. */
  hourRounding: 'CEIL' as const,
  /** NOT all-purpose — flat emission; casual loading does NOT compound. */
  appliesToCasualLoading: false as const,
  clauseRef:
    'Aged Care Award MA000018 cl 15.5(a) (nauseous work allowance — per hour or part thereof, subject to the resolved weekly minimum)',
};

// ═══════════════════════════════════════════════════════════════════════
// Meal-OT emission semantics (cl 15.4(a)(i) + cl 15.4(a)(ii)) — Phase W4
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase W4 (Award Engine Integrity 2026-05-17) — Aged Care meal-OT
 * allowance emission semantics per MA000018 cl 15.4(a)(i) + cl 15.4(a)(ii).
 *
 * Verbatim cl 15.4(a) (FWC consolidated MA000018, primary-source verified
 * W4 specialist invocation):
 *
 *     "(a) An employee required to work overtime for more than one hour
 *          past the usual finishing time, shall either be:
 *            (i)  supplied with an adequate meal where an employer has
 *                 adequate cooking and dining facilities; or
 *                 be paid a meal allowance of $16.62; and
 *            (ii) where such overtime work exceeds four hours, a further
 *                 meal allowance of $14.98 will be paid."
 *
 * Engine emission rules (W4 scope):
 *   - First meal ($16.62, cl 15.4(a)(i)) fires when:
 *       `overtimeHours > 1.0` AND
 *       `!input.mealProvidedDuringOT` AND
 *       `!input.couldReturnHomeForMeal`
 *   - Second meal ($14.98, cl 15.4(a)(ii)) fires when:
 *       first meal triggered (per cumulative semantics — verbatim "a further")
 *       AND `overtimeHours > 4.0`
 *   - The two allowances are CUMULATIVE, not mutually exclusive. A worker
 *     doing 5h OT gets $16.62 + $14.98 = $31.60 total (assuming no carve-out).
 *   - Provision-of-meal carve-out (cl 15.4(a)(i) "supplied with an adequate
 *     meal where an employer has adequate cooking and dining facilities") is
 *     captured via `input.mealProvidedDuringOT`. When true, both allowances
 *     are suppressed.
 *   - The `input.couldReturnHomeForMeal` field is a rare exceptional
 *     carve-out (worker could feasibly return home for the meal); also
 *     suppresses emission when true. Both default to `false` for the W4
 *     emission path; coordinator UI to set them is W4.1.
 *   - Both allowances are NOT all-purpose — Pay Guide page 24 row carries
 *     NO asterisk. Emitted FLAT; casual loading does NOT compound.
 *
 * Engine derivability: `overtimeHours > 1.0` is a close approximation of
 * "more than one hour past the usual finishing time" for single-extended-
 * shift OT. The W4 implementation uses OT-hours-only derivation as the
 * canonical path; the two carve-out flags (`mealProvidedDuringOT`,
 * `couldReturnHomeForMeal`) are the rare exception suppressors.
 *
 * **Cross-award native symmetry (binding per runbook §7 per-award isolation)**:
 * Nurses Award MA000034 cl 17.3(b) AND HPSS Award MA000027 cl 23.3(e) BOTH
 * have STRUCTURALLY IDENTICAL meal-OT shape to MA000018 cl 15.4 (same
 * template, same trigger phrasing, same $16.62 first + $14.98 further
 * amounts; variation cycle PR786721/PR786738/PR786730 all ppc 01Jul25).
 * SCHADS MA000100 cl 20.5(a) anomalously publishes BOTH first + further
 * at $16.62 (not $14.98 further) per FWC allowance sheet (last updated
 * 23 Jun 2026) — see W21 backlog for SCHADS audit. Per
 * `project_cross_award_shape_porting_risk.md`, encode the MA000018
 * constants INDEPENDENTLY here — DO NOT factor `MealOtRules` into
 * `_shared.ts`. The Aged Care + Nurses + HPSS symmetry is genuine, not
 * a port — encode each independently.
 *
 * **Errata #34 (Phase 1O 2026-05-17)**: this docstring previously (pre-
 * Phase-1O) cited HPSS cl 18.7(a) at $13.29 / $11.98. W22 primary-source
 * verification (FWC consolidated MA000027 PDF amendments to 1 Apr 2026)
 * confirmed the correct HPSS provision is cl 23.3(e) at $16.62 / $14.98
 * (DB rate-registry seed at `supabase/migrations/20260610100000_seed_hpss_v1.sql:199`
 * already has $14.98 — engine read was correct; only the W4 docstring
 * was wrong). The W4 work did not primary-source verify the HPSS reference;
 * the W22 specialist disambiguated. See HPSS_MEAL_OT_EMISSION in
 * `award-data/hpss.ts` for the corrected provision + verbatim text.
 *
 * Source: FWC consolidated MA000018 cl 15.4(a) + FWO Pay Guide G01002985
 * (republished 15 Jan 2026) page 24.
 * Verified: 2026-05-17 (W4 aged-care-legislation specialist).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-aged-care-production-readiness-emission-paths.md
 */
export const AGED_CARE_MEAL_OT_EMISSION = {
  awardCode: 'MA000018' as const,
  /** DB lookup key for first meal $16.62 (PER_INCIDENT basis, verified W4). */
  firstMealAllowanceCode: 'MEAL' as const,
  firstMealAmount: 16.62 as const,
  /** OT must exceed this many hours past usual finish for first meal to fire. */
  firstMealThresholdHours: 1.0 as const,
  /** DB lookup key for additional meal $14.98 (PER_INCIDENT, verified W4). */
  secondMealAllowanceCode: 'MEAL_OT_ADDITIONAL' as const,
  secondMealAmount: 14.98 as const,
  /** Cumulative OT must exceed this many hours for second meal to fire (in addition to first). */
  secondMealThresholdHours: 4.0 as const,
  /** CUMULATIVE semantics: second meal is ADDED to first, not exclusive. */
  cumulative: true as const,
  /** NOT all-purpose — flat emission; casual loading does NOT compound. */
  appliesToCasualLoading: false as const,
  clauseRefFirst:
    'Aged Care Award MA000018 cl 15.4(a)(i) (meal allowance — OT > 1h past usual finish; supplied-meal carve-out applies)',
  clauseRefSecond:
    'Aged Care Award MA000018 cl 15.4(a)(ii) (further meal allowance — OT > 4h cumulative; additive to first meal)',
};

// ═══════════════════════════════════════════════════════════════════════
// Leading-hand all-purpose emission semantics (cl 15.3) — Phase W4.0.5
// ═══════════════════════════════════════════════════════════════════════

/**
 * Phase W4.0.5 (Award Engine Integrity 2026-05-17) — Aged Care leading-hand
 * "all-purpose" allowance emission semantics per MA000018 cl 15.3.
 *
 * Verbatim cl 15.3 (FWC consolidated MA000018, primary-source verified
 * W4 + W4.0.5 specialist invocations 2026-05-17):
 *
 *     "(a) A leading hand is an employee who is placed in charge of not
 *          less than two other employees of a substantially similar
 *          classification, but does not include any employee whose
 *          classification denotes supervisory responsibility.
 *      (b) A leading hand receives a weekly allowance as a percentage of
 *          the standard rate based on number of employees supervised:
 *            2-5  employees: 2.67% of standard rate ($32.01/wk)
 *            6-10 employees: 3.81% of standard rate ($45.68/wk)
 *            11-15 employees: 4.81% of standard rate ($57.67/wk)
 *            16+  employees: 5.88% of standard rate ($70.50/wk)
 *      (c) The allowance forms part of salary for all purposes.
 *      (d) Part-time workers receive a pro-rated allowance based on
 *          average hours worked each week."
 *
 * **All-purpose mechanics — STRUCTURAL change to `calculate()` flow:**
 *
 * Per cl 15.3(c) verbatim "part of salary for **all purposes**" + Pay Guide
 * page 24 asterisk footer ("If any all purpose allowances apply (marked *),
 * they should be added to the minimum rate before penalties and overtime
 * are calculated.") + FWO library K600467 ("all-purpose ... included when
 * calculating overtime, leave, and super") — three converging primary
 * sources confirm:
 *
 *   `baseHourlyRate = resolvedRate.hourlyRate + leadingHandPerHour`
 *
 * applied BEFORE every penalty (cl 26 Saturday/Sunday/PH), shift loading
 * (cl 26 afternoon/night), and OT (cl 25) multiplier. The lifted rate
 * propagates through all downstream band calculations automatically.
 *
 * Worked example confirming option (a) flat-base-lift vs option (b) flat-emit:
 * Aged Care direct_care L3 ($28.62/hr base) + leading-hand 6-10 ($1.20/hr)
 * on a Saturday shift (1.5× penalty):
 *   - WRONG (flat emit): `$28.62 × 1.5 + $1.20 = $44.13/hr`
 *   - CORRECT (all-purpose): `($28.62 + $1.20) × 1.5 = $44.73/hr`
 *   - Difference: $0.60/hr UNDER-PAY per flat-emit error
 *
 * **Per-hour conversion precision (specialist recommendation 2026-05-17):**
 *
 * Store WEEKLY amounts in DB (already seeded $32.01/$45.68/$57.67/$70.50);
 * compute hourly = weekly / 38 ordinary hours AT RUNTIME. Do NOT bake
 * rounded $0.84/$1.20/$1.52/$1.86 per-hour constants — they produce ~0.3%
 * accumulated drift across multi-shift pay runs. Engine reads WEEKLY from
 * `ctx.allowances` row then divides by `weeklyDivisor` for exact compute.
 *
 * **Per-shift binary granularity (specialist confirmed 2026-05-17):**
 *
 * cl 15.3(b) "weekly allowance" + cl 15.3(d) "average hours worked each
 * week" + zero partial-shift language in cl 15.3 = whole-shift designation.
 * Worker is leading-hand for the entire shift OR not. Engine input
 * `leadingHandSupervisedCount` is per-shift; lift applies to ALL hoursWorked
 * on the shift. Per-hour granular (`leadingHandHoursOnShift < shiftHours`)
 * REJECTED — not a real scenario per primary-source verification.
 *
 * **Eligibility — engine trusts caller (specialist Option B confirmed):**
 *
 * cl 15.3(a) "denotes supervisory responsibility" is a QUALITATIVE test
 * with no enumerated exclusion list in award text + no FWC determination +
 * no FWO library article. Engine TRUSTS the caller's `leadingHandSupervisedCount`
 * input without validating classification eligibility. The coordinator UI
 * (W4.1) must surface the carve-out: direct_care L6 "Team Leader" is
 * presumptively excluded (classification name itself denotes supervision);
 * general L7 borderline; all other classifications eligible. Engine warns
 * when count <2 (sub-minimum, no lift) but does NOT validate classification.
 *
 * **Cross-award native isolation (binding per runbook §7 + W4.0.5 specialist):**
 *
 * MA000018 cl 15.3 LEADING_HAND has **NO equivalent** in Nurses MA000034 /
 * HPSS MA000027 / SCHADS MA000100. The W4.0.5 specialist re-confirmed
 * (2026-05-17) and additionally caught a potential shape-port: Nurses
 * Schedule A.4.6 "Enrolled nurse supervising other direct care employees"
 * is a CLASSIFICATION REASSIGNMENT (separate higher weekly minimum
 * $1,409.60/wk), NOT a per-shift allowance. No W22/W23/W24 spawning.
 *
 * Source: FWC consolidated MA000018 cl 15.3 (verbatim with sub-clauses
 * (a)/(b)/(c)/(d)) + FWO Pay Guide G01002985 (republished 15 Jan 2026)
 * page 24 + FWO library K600467 + FWC allowance summary MA000018
 * footnote 1 "applies for all purposes of this award".
 * Verified: 2026-05-17 (W4 + W4.0.5 aged-care-legislation specialists).
 * Artifacts:
 *   - docs/operations/clause-verifications/2026-05-17-aged-care-legislation-aged-care-production-readiness-emission-paths.md (W4)
 *   - docs/operations/clause-verifications/2026-05-17-aged-care-legislation-aged-care-leading-hand-all-purpose-refactor.md (W4.0.5)
 */
export const AGED_CARE_LEADING_HAND_EMISSION = {
  awardCode: 'MA000018' as const,
  /**
   * Worker must supervise at LEAST 2 other employees for any tier to fire
   * (cl 15.3(a) "not less than two other employees"). 0 or 1 → no lift.
   */
  minimumSupervisedCount: 2 as const,
  /**
   * Ordinary hours/week divisor for per-hour conversion from the weekly
   * allowance amount (cl 13.1 ordinary hours; cl 15.3(d) part-time pro-rata
   * anchors to this denominator). Pay Guide hourly figures
   * ($0.84/$1.20/$1.52/$1.86) are derived as `weekly / 38`.
   */
  weeklyDivisor: 38 as const,
  /**
   * All-purpose: per-hour amount lifts `baseHourlyRate` BEFORE every
   * penalty/OT/shift-loading multiplier. See full provenance above.
   */
  allPurpose: true as const,
  /**
   * Tier-headcount map. Each tier's DB allowance code resolves to a
   * PER_WEEK_FT amount (already seeded; values verified Phase 1I.5).
   * Boundaries inclusive on both ends (Pay Guide + specialist confirmed):
   *   - 2-5: count ∈ {2, 3, 4, 5}
   *   - 6-10: count ∈ {6, 7, 8, 9, 10}
   *   - 11-15: count ∈ {11, 12, 13, 14, 15}
   *   - 16+: count ≥ 16 (no upper bound)
   * Worker counts OTHER workers, not themselves: 1 LH + 5 colleagues = 5.
   */
  tiers: [
    { minSupervised: 2, maxSupervised: 5, allowanceCode: 'LEADING_HAND_2_5' as const },
    { minSupervised: 6, maxSupervised: 10, allowanceCode: 'LEADING_HAND_6_10' as const },
    { minSupervised: 11, maxSupervised: 15, allowanceCode: 'LEADING_HAND_11_15' as const },
    {
      minSupervised: 16,
      maxSupervised: null,
      allowanceCode: 'LEADING_HAND_16_PLUS' as const,
    },
  ] as const,
  clauseRef:
    'Aged Care Award MA000018 cl 15.3 (leading hand allowance — all-purpose per cl 15.3(c) "part of salary for all purposes"; per-shift binary; engine trusts caller for classification eligibility; W4.1 UI surfaces supervisory-classification carve-out)',
};

/**
 * Phase W4.0.5 — resolve the tier-applicable allowance code for a given
 * supervised count. Returns `null` if the count is sub-minimum
 * (`< minimumSupervisedCount`) or `null/undefined` (not in charge).
 *
 * Pure function; no side effects. Suitable for direct use in both the
 * engine and any future workflow / UI layer that needs to predict the
 * applicable tier without invoking the full engine.
 */
export function resolveLeadingHandTier(
  supervisedCount: number | null | undefined,
): (typeof AGED_CARE_LEADING_HAND_EMISSION.tiers)[number] | null {
  if (
    supervisedCount == null ||
    supervisedCount < AGED_CARE_LEADING_HAND_EMISSION.minimumSupervisedCount
  ) {
    return null;
  }
  for (const tier of AGED_CARE_LEADING_HAND_EMISSION.tiers) {
    if (
      supervisedCount >= tier.minSupervised &&
      (tier.maxSupervised == null || supervisedCount <= tier.maxSupervised)
    ) {
      return tier;
    }
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// Leave entitlements (NES + MA000018)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Annual leave loading rate for Aged Care Award MA000018.
 *
 * Phase 3 PR #3 sub-phase 3G (2026-05-18) — engine emission of
 * LEAVE_LOADING_ALLOWANCE on paid_annual_leave segments for non-casual
 * employees. v1 naive 17.5% × ordinary leave pay only; cl 28.4(b)
 * shiftworker higher-of test (compare 17.5% loading vs Saturday/Sunday/PH
 * penalty rates the worker would have earned) DEFERRED to W35 post-Madea
 * per Phase 3 PR #3 scope.
 *
 * Engine emits the flat 17.5% per cl 28.4(a) day-worker rule for ALL
 * employees. The shiftworker higher-of comparison is applied by the pay-run
 * leave-snapshot layer from rostered shift evidence.
 *
 * Casuals are excluded — NES s.86 + cl 28.4 (annual leave is a permanent
 * entitlement; casuals receive 25% loading on ordinary hours in lieu).
 *
 * Source: FWC consolidated MA000018 cl 28.4 (annual leave loading; PDF
 * incorporated 23 Jan 2026, pp.47-48); clause renumbered 28.3 → 28.4 by
 * PR779150 ppc 01 Jan 2025. NES s.86 (annual leave applies only to
 * non-casual employees).
 * Verified: 2026-05-19 (specialist primary-source check; pre-fix
 * incorrectly cited cl 30.4 which is Personal/carer's leave).
 */
export const AGED_CARE_LEAVE = {
  /** 17.5% loading on paid annual leave ordinary hours. */
  annualLeaveLoading: 0.175,
  /** cl 28.4(a) — Aged Care Award MA000018 (day-worker flat rule). */
  clauseRef:
    'Aged Care Award MA000018 cl 28.4(a) (annual leave loading — 17.5% on ordinary leave pay; cl 28.4(b) shiftworker higher-of applied by pay-run leave snapshot from roster evidence)',
} as const;
